/**
 * generate[v](ds, brand) → Tokens — PURE and DETERMINISTIC. Runs identically in
 * the browser (preview) and workerd (publish): no DOM/Canvas, no Date/random,
 * locale-independent, fixed rounding. Color math via culori (OKLCH) → hex.
 *
 * Versioned: a site pins the `generationVersion` that produced its look, so
 * improving the generator never silently restyles existing sites. Add a new
 * version as a new entry; NEVER mutate an existing one.
 */
/// <reference path="./culori.d.ts" />
import { converter, formatHex, parse, wcagContrast } from "culori";
import type { BrandInputs, CornerRoundness, Density } from "./brand.js";
import type { DesignSystem } from "./designSystem.js";
import type {
  ColorScale,
  ColorScaleStep,
  RadiusTokens,
  SpaceTokens,
  Tokens,
} from "./tokens.js";

export const CURRENT_GENERATION_VERSION = 1;

type Generator = (ds: DesignSystem, brand: BrandInputs) => Tokens;

const toOklch = converter("oklch");

const STEPS: readonly ColorScaleStep[] = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
];
// Perceptual lightness targets per step (OKLCH L). 500 is the mid anchor.
const TARGET_L: Record<ColorScaleStep, number> = {
  "50": 0.971,
  "100": 0.936,
  "200": 0.866,
  "300": 0.777,
  "400": 0.688,
  "500": 0.606,
  "600": 0.541,
  "700": 0.466,
  "800": 0.386,
  "900": 0.305,
};
const MID_L = TARGET_L["500"];

const round = (n: number, d = 4): number => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

/** Build a perceptual scale at a fixed hue, tapering chroma toward the extremes. */
function buildScale(hue: number, chroma: number): ColorScale {
  const out = {} as Record<ColorScaleStep, string>;
  for (const step of STEPS) {
    const l = TARGET_L[step];
    const taper = 1 - Math.abs(l - MID_L) / MID_L; // 1 at 500, →0 at the ends
    const c = round(chroma * Math.max(0.4, taper));
    out[step] = formatHex({ mode: "oklch", l, c, h: hue }) ?? "#000000";
  }
  return out;
}

function scaleFromSeed(hex: string): ColorScale {
  const o = toOklch(parse(hex));
  return buildScale(o?.h ?? 265, round(o?.c ?? 0.15));
}

/** Near-neutral scale sharing the brand hue with a faint tint of chroma. */
function neutralFromSeed(hex: string): ColorScale {
  const o = toOklch(parse(hex));
  return buildScale(o?.h ?? 265, 0.01);
}

/** Pick #fff or #111 for legible text on a background. */
function onColor(bg: string): string {
  return wcagContrast(bg, "#ffffff") >= wcagContrast(bg, "#111111")
    ? "#ffffff"
    : "#111111";
}

const ROUND_BASE: Record<CornerRoundness, number> = {
  none: 0,
  subtle: 4,
  rounded: 8,
  pill: 9999,
};
function radiusFrom(r: CornerRoundness): RadiusTokens {
  if (r === "pill")
    return { none: 0, sm: 9999, md: 9999, lg: 9999, full: 9999 };
  if (r === "none") return { none: 0, sm: 0, md: 0, lg: 0, full: 9999 };
  const b = ROUND_BASE[r];
  return { none: 0, sm: Math.round(b / 2), md: b, lg: b * 2, full: 9999 };
}

const DENSITY_UNIT: Record<Density, number> = {
  compact: 3,
  comfortable: 4,
  spacious: 5,
};
function spaceFrom(d: Density): SpaceTokens {
  const u = DENSITY_UNIT[d];
  return {
    xs: u,
    sm: u * 2,
    md: u * 4,
    lg: u * 6,
    xl: u * 8,
    "2xl": u * 12,
    "3xl": u * 16,
  };
}

const generateV1: Generator = (ds, brand) => {
  const primary = scaleFromSeed(brand.brandColor);
  const neutral = neutralFromSeed(brand.brandColor);
  // A colour field writes every keystroke, so a half-typed `#f` reaches here
  // and is persisted. An accent that does not parse falls back to the brand's
  // own mid tone, like `scaleFromSeed` does for the brand, instead of flowing
  // on raw into `onColor`, where it threw on every load and every render.
  const accentSolid = formatHex(parse(brand.accentColor)) ?? primary["500"];
  const base = ds.defaultTokens;

  return {
    color: {
      primary,
      neutral,
      background: "#ffffff",
      foreground: neutral["900"],
      primarySolid: primary["600"],
      primaryForeground: onColor(primary["600"]),
      accent: accentSolid,
      accentForeground: onColor(accentSolid),
      muted: neutral["100"],
      mutedForeground: neutral["500"],
      border: neutral["200"],
      ring: primary["500"],
    },
    space: spaceFrom(brand.density),
    radius: radiusFrom(brand.cornerRoundness),
    typography: {
      ...base.typography,
      fontFamily: {
        heading: brand.headingFont,
        body: brand.bodyFont,
        mono: base.typography.fontFamily.mono,
      },
    },
    // Non-Brand-driven groups are the design system's own taste.
    shadow: base.shadow,
    border: base.border,
  };
};

const GENERATORS: Record<number, Generator> = {
  1: generateV1,
};

/**
 * Derive the full token set. `version` defaults to the current generator; pass a
 * site's pinned `generationVersion` to reproduce its exact look.
 */
export function generate(
  ds: DesignSystem,
  brand: BrandInputs,
  version: number = CURRENT_GENERATION_VERSION,
): Tokens {
  const gen = GENERATORS[version];
  if (!gen) {
    throw new Error(
      `theme-engine: unknown generationVersion ${version} (known: ${Object.keys(GENERATORS).join(", ")})`,
    );
  }
  return gen(ds, brand);
}
